import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { EquipmentBase } from '../../planogram/models/equipment-base';
import { Element } from '../../equipment/models/elements/element';
import { elementsGenerator } from '../../equipment/models/elements/elementGenerator';
import { BarrierParams, Extension, ExtensionType, getSavedBarrierFields, RoundingParams } from '../../equipment/models/elements/extension';
import { ShelfElement } from '../../planogram/models/elements/shelfElement';
import { HangBarElement } from '../../planogram/models/elements/hangBarElement';
import { HookElement } from '../../planogram/models/elements/hookElement';
import { HoleElement } from '../../planogram/models/elements/holeElement';
import { BoxElement } from '../../planogram/models/elements/boxElement';
import { PalletElement } from '../../planogram/models/elements/palletElement';
import { Equipment } from '../../equipment/models/equipment';
import { IConfElement, IEquipment, IEquipmentConfigurations, IEquipmentType } from '../../equipment/models/models';
import { Observable, forkJoin, of, switchMap, timer } from 'rxjs';
import { ITableData } from '../models/table';
import { IEquipmentListItem } from '../../equipment/models/equipmentListItem';
import { ICloneItems } from './plans.service';
import { AsyncValidatorFn, FormControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { SnackBarService } from './snack-bar.service';
import { Figure } from '../../equipment/models/figures/figure';
import { Utils } from './utils.service';
import { IExcelTemplateDataCheck, IExcelTemplatesCheckResult } from '../../equipment/models/excelTemplateCheck';
import { DwUsersActivityService } from './dw-users-activity.service';
import { DwUserActivityNames } from '../models/dwUserActivityNames';
import { DwUserActivityTypes } from '../models/dwUsersActivityTypes';
import { NOT_CANCELLED_HEADER } from '../interceptors/auth.interceptor';

export const ROOT_URL: string = 'api/layout/instruments/', NEW_ROOT_URL: string = 'api/v1/equipment/';

export interface IEquipmentUpdatePayload {
	id: number;
	params: Record<string, unknown>;
}

@Injectable({
	providedIn: 'root'
})
export class EquipmentService {
	listLoadParams: string = null;

	static getEquipmentConfigurations(equipment: Equipment): IEquipmentConfigurations {
		equipment.cachedRows.clear();
		let linearConf: any = {
				linear_meters: equipment.LinearMeters,
				square_meters: equipment.SquareMeters
			},
			conf: { elements?: Array<any> } = {},
			newConf = {};

		if (!equipment.Empty) {
			const moveWidth: number = equipment.bases
				.sort((a, b) => a.shapes[0].begin.x - b.shapes[0].begin.x)[0].shapes[0].begin.x;

			equipment.allElements.forEach((b) =>
				b.shapes.forEach((s) =>
					moveWidth > 0
						? (s.begin.x -= moveWidth)
						: (s.begin.x += Math.abs(moveWidth)))
			);

			const width = equipment.Width,
				height = equipment.Height,
				depth = equipment.Depth;

			linearConf = {
				width,
				depth,
				height,
				linear_meters: equipment.LinearMeters,
				square_meters: equipment.SquareMeters,
				bases: equipment.bases
					.sort((a: Element, b: Element) => a.shapes[0].begin.x - b.shapes[0].begin.x)
					.map((i) => {
						const rounding: Extension<RoundingParams> = i.getExtension(ExtensionType.Rounding);
						return {
							parent: i.parent.type,
							shapes: i.shapes.map(shape => ({
								...shape.savedParams,
								leftSideRadius: rounding?.active ? rounding.params?.leftSideRadius ?? 0 : 0,
								rightSideRadius: rounding?.active ? rounding?.params?.rightSideRadius ?? 0 : 0
							}))
						}
				})
			};

			const sortedElements: Array<Element> = Utils.getSortedElementsByDepend(equipment.allElements);
			conf = {
				elements: sortedElements
					.filter((el) => !el.parent.type.includes('Base'))
					.map((el) => {
						const { depend, parent, shapes } = el, dependIndex = sortedElements.indexOf(depend);

						return {
							dependIndex,
							number: Utils.getElementIndex(el, equipment),
							parent: parent.type,
							shapes: shapes.map(shape => shape.savedParams)
						};
					})
			};

			const newElements: { [key: string]: IConfElement } = {};
			sortedElements.forEach((element: Element, index: number, array: Array<Element>) => {
				const { depend, parent, shapes } = element,
					dependId = equipment.allElements.find((el) => el === depend)?.id || null;

				newElements[element.id] = {
					id: element.id,
					dependId: dependId,
					number: Utils.getElementIndex(element, equipment),
					next: index + 1 === array.length ? null : array[index + 1].id,
					parent: parent.type,
					shapes: shapes.map((shape: Figure) => shape.savedParams),
					extensions: element.extensions.map((extension: Extension<any>) => {
						if (extension.type === ExtensionType.Barrier) {
							return {
								type: extension.type,
								...getSavedBarrierFields(extension.active, extension.params as BarrierParams)
							};
						}

						return { type: extension.type, active: extension.active, ...extension.params };
					})
				};
			});

			newConf = {
				elements: newElements
			};
		}
		return {
			conf,
			newConf,
			linearConf
		};
	}

	constructor(
		private router: Router,
		private http: HttpClient,
		private httpService: HttpService,
		private sessionService: SessionService,
		private snackBarService: SnackBarService,
		private dwUsersActivityService: DwUsersActivityService
	) {}

	bulkClone(params: any = {}, newNames: Array<ICloneItems> = []): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.Equipment, DwUserActivityTypes.Copy);

		return this.http.post<void>(NEW_ROOT_URL + 'clone/', newNames, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	bulkDelete(params: any = {}): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.Equipment, DwUserActivityTypes.Delete);

		return this.http.delete<void>(NEW_ROOT_URL, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	bulkUpdate(params: any, body: { type?: number, producer?: string } = {}): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.Equipment, DwUserActivityTypes.Edit);

		return this.http.post<void>(`${NEW_ROOT_URL}bulk_update/`, body, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	checkNameUnique(name: string): Observable<{ exists: boolean }> {
		return this.http.post<{ exists: boolean }>(NEW_ROOT_URL + 'check_name/', { name: name });
	}

	create(equipment: any): Observable<IEquipment> {
		this.dwUsersActivityService.track(DwUserActivityNames.Equipment, DwUserActivityTypes.Create);

		return this.http.post<IEquipment>(NEW_ROOT_URL, { ...equipment });
	}

	createType(name: string): Observable<IEquipmentType> {
		return this.http.post<IEquipmentType>('api/v1/equipment-types/', { name: name });
	}

	delete(id: number): Promise<any> {
		this.dwUsersActivityService.track(DwUserActivityNames.Equipment, DwUserActivityTypes.Delete);

		return this.httpService.getPromise<any>(
			this.http.delete<any>(ROOT_URL + `${id}/`, { observe: 'response' })
		);
	}

	get(id: number): Promise<IEquipment> {
		return this.httpService.getPromise<IEquipment>(
			this.http.get<IEquipment>(ROOT_URL + `${id}/?version=2`)
		);
	}

	get$(id: number): Observable<IEquipment> {
		return this.http
			.get<IEquipment>(ROOT_URL + `${id}/?version=2`)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					if (error.status === 404) {
						this.router.navigate(this.sessionService.getLink(['equipment'])).then(() => {
							this.snackBarService.setSnackBarNotification({
								messageText: 'COMMON.EQUIPMENT.NOT_EXIST'
							});
						});
					}
					return of(null)
				})
			);
	}

	getEquipmentListTable(params: any = {}): Observable<ITableData<IEquipmentListItem>> {
		return this.http.get<ITableData<IEquipmentListItem>>('api/v1/equipment-table/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getEquipmentUsedCount(params: any = {}): Observable<{ used: number, not_used: number }> {
		return this.http.get<{ used: number, not_used: number }>(NEW_ROOT_URL + 'usage-count/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getList(params: { detail: boolean, empty: boolean } = { detail: true, empty: true }): Promise<Array<IEquipment>> {
		const parameters: { detail?: boolean, empty?: boolean } = {};
		if (params.detail) parameters.detail = true;
		if (!params.empty) parameters.empty = false;

		return this.httpService.getPromise<Array<IEquipment>>(
			this.http.get<Array<IEquipment>>(ROOT_URL, {
				params: new HttpParams({
					fromObject: parameters
				})
			})
		);
	}

	getList$(params: { detail: boolean, empty: boolean } = { detail: true, empty: true }): Observable<Array<IEquipment>> {
		const parameters: { detail?: boolean, empty?: boolean } = {};
		if (params.detail) parameters.detail = true;
		if (!params.empty) parameters.empty = false;

		return this.http.get<Array<IEquipment>>(ROOT_URL, {
			params: new HttpParams({ fromObject: parameters })
		});
	}

	getTypes(): Promise<Array<IEquipmentType>> {
		return this.httpService.getPromise<Array<IEquipmentType>>(
			this.http.get<Array<IEquipmentType>>(`api/layout/instrument-types/`)
		);
	}

	nameUniqueValidator(exclusion: Array<string> = []): AsyncValidatorFn {
		return (control: FormControl<string>): Observable<ValidationErrors | null> => {
			return timer(500).pipe(
				switchMap(() => {
					const trimmedValue: string = (control.value || '').trim();
					if (!trimmedValue) {
						return of(null);
					}
					const normalizedExclusion: Array<string> = exclusion.map((item: string) => (item || '').trim());
					if (normalizedExclusion.some((item: string) => item === trimmedValue)) {
						return of(null);
					}
					return this.checkNameUnique(trimmedValue).pipe(
						// Ignore offline/network errors to avoid false "name exists" errors.
						catchError(() => of({ exists: false })),
						map((response: { exists: boolean }) => (response.exists ? { exists: true } : null))
					);
				})
			);
		}
	}

	setEquipmentConfElements(stand: EquipmentBase, confElements: { [id: string]: IConfElement }): void {
		if (!confElements) return;

		const confElementsArray: Array<IConfElement> = Object.values(confElements),
			nextIds: Array<string> = confElementsArray.map((element) => element.next),
			rootElement: IConfElement = confElementsArray.find((element) => !nextIds.includes(element.id));

		const addElement = (element: IConfElement) => {
			let addedElement: Element = elementsGenerator.createElement(element.parent, element.shapes, element.extensions, element.id);

			if (['Shelf', 'AngleShelf'].includes(addedElement.parent.type)) addedElement = new ShelfElement(addedElement);
			if (['HangBar'].includes(addedElement.parent.type)) addedElement = new HangBarElement(addedElement);
			if (['Hook'].includes(addedElement.parent.type)) addedElement = new HookElement(addedElement);
			if (['Hole'].includes(addedElement.parent.type)) addedElement = new HoleElement(addedElement);
			if (['Box', 'SlicedBox'].includes(addedElement.parent.type)) addedElement = new BoxElement(addedElement);
			if (['RectangleBase', 'TrapezeBase'].includes(addedElement.parent.type)) {
				if (confElementsArray.some((e) => e.dependId === addedElement.id)) {
					addedElement = new ShelfElement(addedElement);
				} else {
					addedElement = new PalletElement(addedElement);
				}
			}

			stand.addElement(addedElement);
			addedElement.stand = stand;
			if (!element.next) return;
			addElement(confElements[element.next]);
		};

		addElement(rootElement);

		confElementsArray.forEach((confElement: IConfElement) => {
			const thisElement: Element = stand.allElements.find((element: Element) => element.id === confElement.id);
			thisElement.depend = stand.allElements.find((element: Element) => element.id === confElement.dependId);
		});

		stand.allElements.forEach((el: Element, index: number) => {
			el.idx = index;
			if (el.constructor.name === 'ShelfElement') (el as ShelfElement).setPlacementHeight(stand);
		});
	}

	templateDataCheck(data: Array<IExcelTemplateDataCheck>): Observable<IExcelTemplatesCheckResult> {
		this.dwUsersActivityService.track(DwUserActivityNames.Equipment, DwUserActivityTypes.Upload);

		return this.http.post<IExcelTemplatesCheckResult>('/api/v1/equipment-templates/', { data: data });
	}

	templatesExcelCheck(file: File): Observable<IExcelTemplatesCheckResult> {
		const formData: FormData = new FormData();
		formData.set('file', file);

		return this.http.post<IExcelTemplatesCheckResult>('/api/v1/equipment-templates/check-excel/', formData);
	}

	bulkSave(updates: Array<IEquipmentUpdatePayload>): Observable<void> {
		if (!updates.length) return of(void 0);

		return forkJoin(
			updates.map((item: IEquipmentUpdatePayload) => this.update(item.id, item.params))
		).pipe(map(() => void 0));
	}

	update(id: number, params: any = {}): Observable<any> {
		this.dwUsersActivityService.track(DwUserActivityNames.Equipment, DwUserActivityTypes.Edit);

		return this.http.patch(`${NEW_ROOT_URL}${id}/`, params, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') });
	}
}
